/**
 * dcent-app-home-demo.js — 앱 홈 화면 목업의 재생 제어.
 *
 * 마크업은 `snippets/dcent-app-home-demo.liquid`, 외형은 `dcent-app-home-demo.css`.
 * 원본: `20260827 homepage_전달용/src/components/WalletHomeDemo.tsx` 의 useEffect + CountUp.
 *
 * 하는 일은 셋뿐이다. 나머지 연출은 전부 CSS 가 갖는다.
 *   1. data-rise(ms) → CSS 변수로 옮긴다. 값이 13종이라 CSS 에 다 적으면 규칙만 불어난다
 *   2. 잔액·등락 카운트업(1.4s ease-out)
 *   3. 보일 때 data-on=1, 벗어나면 리셋
 *
 * ⚠️ **frozen 이거나 reduced-motion 이면 완성 화면을 그린다.** 그냥 멈추면 잔액이 0 이고
 * 차트가 덮개에 가려진 빈 화면이 남아 "자산이 한눈에 보인다"는 요지가 사라진다.
 * 모션만 빼고 정보는 남기는 것이 이 사이트 전반의 규약이다.
 *
 * 차트 덮개는 다 열리면 **DOM 에서 뺀다** — 남겨두면 화면마다 상시 합성 레이어가 하나씩
 * 쌓인다(한 페이지에 이 데모가 셋이다).
 *
 * 안전 규약: 대상 없으면 조용히 종료 · 멱등 설치 · 보일 때만 재생 · 이탈 시 리셋.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */

if (!window.__dcentAppHomeDemo) {
  window.__dcentAppHomeDemo = true;

  const DUR = 1400;

  /** 완성값을 그대로 되돌려 놓기 위해 첫 렌더의 텍스트를 기억해 둔다. */
  const setupCounter = (el) => {
    const to = Number(el.dataset.countTo);
    const dec = Number(el.dataset.countDec) || 0;
    const pre = el.dataset.countPrefix || "";
    const suf = el.dataset.countSuffix || "";
    const done = el.textContent;
    const fmt = (v) =>
      pre +
      v.toLocaleString("en-US", {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      }) +
      suf;
    return { el, to, done, fmt };
  };

  const setup = (root) => {
    const risers = Array.from(root.querySelectorAll("[data-rise]"));
    risers.forEach((n) => {
      n.style.setProperty("--dcent-ahd-delay", `${n.dataset.rise}ms`);
    });

    const counters = Array.from(
      root.querySelectorAll("[data-count-to]"),
    ).map(setupCounter);
    const cover = root.querySelector(".dcent-ahd__chart-cover");

    // 완성 상태로 정지 — 빈 화면을 남기지 않는다
    const freeze = () => {
      root.dataset.still = "1";
      root.dataset.on = "1";
      counters.forEach((c) => {
        c.el.textContent = c.done;
      });
      if (cover) cover.remove();
    };

    if (
      root.dataset.frozen === "1" ||
      (window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    ) {
      freeze();
      return;
    }

    let raf = 0;
    const count = (playing) => {
      cancelAnimationFrame(raf);
      if (!playing) {
        counters.forEach((c) => {
          c.el.textContent = c.fmt(0);
        });
        return;
      }
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / DUR);
        const eased = 1 - Math.pow(1 - p, 3);
        counters.forEach((c) => {
          c.el.textContent = p < 1 ? c.fmt(c.to * eased) : c.done;
        });
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    // 덮개는 한 번 다 열리면 뺀다 — 상시 합성 레이어를 남기지 않는다
    if (cover) {
      cover.addEventListener(
        "transitionend",
        () => {
          if (root.dataset.on === "1") cover.remove();
        },
        { once: true },
      );
    }

    count(false);

    if (!("IntersectionObserver" in window)) {
      root.dataset.on = "1";
      count(true);
      return;
    }
    // threshold 0.15 — 히어로는 폰 아래쪽이 크게 잘려 0.4 를 넘지 못한다(원본과 같은 값)
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          root.dataset.on = "1";
          count(true);
        } else {
          root.dataset.on = "0";
          count(false);
        }
      },
      { threshold: 0.15 },
    );
    io.observe(root);
  };

  const init = () => {
    document.querySelectorAll("[data-app-home]").forEach((root) => {
      if (root.dataset.appHomeReady === "1") return; // 멱등
      root.dataset.appHomeReady = "1";
      setup(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
